import { useMemo, useState } from 'react';
import classes from './EventsCards.module.css';
import Modal from '../Modal/Modal';

function parseDate(timestamp) {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(date) {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
    });
}

/** Day / month / year split so the date can be set as a calendar tile. */
function dateParts(date) {
    if (!date) return null;
    return {
        day: date.toLocaleDateString("en-US", { day: "2-digit" }),
        month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
        year: date.getFullYear(),
    };
}

function EventsCards({ data, name }) {
    const [showPopup, setShowPopup] = useState(false);

    const eventData = useMemo(() => {
        const raw = name === 'event'
            ? (data?.eventDate ?? data?.postDate)
            : data?.postDate;
        const date = parseDate(raw);

        if (name === 'event') {
            return {
                title: data?.title,
                description: data?.description,
                date,
                image: data?.image || data?.thumbnailUrl || data?.attachments?.[0] || data?.images?.[0]?.url
            };
        }

        if (name === 'news') {
            return {
                title: data?.title,
                description: data?.body,
                date,
                // `image` is null on some records; the picture is then on
                // thumbnailUrl or the first entry of `images`.
                image: data?.image || data?.thumbnailUrl || data?.images?.[0]?.url
            };
        }

        return {};
    }, [data, name]);

    const parts = dateParts(eventData?.date);

    return (
        <>
            <article className={classes.eventCard} data-card>
                <div className={classes.media}>
                    {/* The frame owns the aspect ratio and the clipping; the
                        image is absolutely positioned inside it so its own
                        height can never feed back into the ratio. The date tile
                        sits outside the frame so it can hang past the edge. */}
                    <div className={classes.mediaFrame}>
                        {eventData?.image ? (
                            <img src={eventData.image} alt="" className={classes.mediaImg} loading="lazy" />
                        ) : (
                            <div className={classes.mediaFallback} aria-hidden="true" />
                        )}
                        <span className={classes.mediaWash} aria-hidden="true" />
                    </div>

                    {/* Calendar tile straddling the image / body edge. */}
                    {parts && (
                        <time
                            className={classes.dateTile}
                            dateTime={eventData.date.toISOString()}
                        >
                            <span className={classes.dateDay}>{parts.day}</span>
                            <span className={classes.dateMonth}>{parts.month}</span>
                        </time>
                    )}
                </div>

                <div className={classes.cardBody}>
                    <h3 className={classes.cardTitle}>{eventData?.title}</h3>
                    <span className={classes.titleRule} aria-hidden="true" />
                    <p className={classes.cardDesc}>{eventData?.description}</p>
                </div>

                <footer className={classes.cardFoot}>
                    {parts && <span className={classes.year}>{parts.year}</span>}
                    {/* Stretched link: the ::after in CSS makes the whole card
                        activate this one control, so there is a single tab stop
                        and no nested interactive elements. */}
                    <button
                        type="button"
                        className={classes.readMore}
                        onClick={() => setShowPopup(true)}
                    >
                        <span className={classes.readMoreText}>Read more</span>
                        <span className={classes.readMoreDisc} aria-hidden="true">
                            <svg viewBox="0 0 16 16">
                                <path
                                    d="M3 8h9M8.5 4l4 4-4 4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </button>
                </footer>
            </article>

            {showPopup && (
                <Modal
                    title={eventData?.title}
                    image={eventData?.image}
                    onClose={() => setShowPopup(false)}
                >
                    {eventData?.date && (
                        <p className={classes.modalDate}>{formatDate(eventData.date)}</p>
                    )}
                    <p>{eventData?.description}</p>
                </Modal>
            )}
        </>
    )
}

export default EventsCards
